import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import { ArrowDownTrayIcon, DocumentTextIcon, PencilIcon } from "@heroicons/react/24/outline";
import { HsePageChrome } from "../HsePageChrome";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import { ResourceError } from "../../../utils/ResourceError";
import {
  evaluationApiError,
  exportEvaluationPdf,
  fetchEvaluation,
} from "../../../../api/hse/evaluations";
import { EvaluationFindingsPanel } from "./EvaluationFindingsPanel";
import { EvaluationLabelInfo } from "./EvaluationLabelInfo";
import {
  EVALUATION_TYPES,
  evaluationPath,
  formatDisplayDate,
  personLabel,
  resultTone,
  statusTone,
} from "./evaluationUtils";

function Meta({ label, value }) {
  return (
    <div>
      <dt className="text-[12px] font-bold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-1 text-sm font-semibold text-slate-900">{value || "—"}</dd>
    </div>
  );
}

export function EvaluationViewPage({ evaluationType, id, report = false }) {
  const { t } = useTranslation();
  const isAudit = evaluationType === EVALUATION_TYPES.INTERNAL_AUDIT;
  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await fetchEvaluation(id, evaluationType);
      if (!data) {
        setError(t("hse.evaluation.view.not_found"));
        setRecord(null);
        return;
      }
      setRecord(data);
    } catch (err) {
      setError(evaluationApiError(err) || t("hse.evaluation.list.load_failed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, evaluationType]);

  const listPath = evaluationPath({ kind: evaluationType });

  const handleExportPdf = async () => {
    if (!record?.id || exporting) return;
    try {
      setExporting(true);
      await exportEvaluationPdf(record.id, `hse-evaluation-${record.id}.pdf`);
    } catch (err) {
      toast.error(evaluationApiError(err) || t("hse.evaluation.export_failed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <HsePageChrome
      title={
        record?.title ||
        t(isAudit ? "hse.evaluation.list.title_audits" : "hse.evaluation.list.title_reviews")
      }
      subtitle={
        report
          ? t("hse.evaluation.view.report")
          : t(isAudit ? "hse.evaluation.nav.audit_hint" : "hse.evaluation.nav.review_hint")
      }
      backTo={listPath}
      mainButton={
        record
          ? {
              label: t("hse.evaluation.view.edit"),
              icon: PencilIcon,
              path: evaluationPath(record, "edit"),
            }
          : undefined
      }
      actions={
        record
          ? [
              {
                label: exporting ? t("hse.evaluation.exporting") : t("hse.evaluation.export_pdf"),
                icon: ArrowDownTrayIcon,
                action: handleExportPdf,
                hidden: false,
                menu: false,
                loading: exporting,
                disabled: exporting,
              },
              isAudit
                ? {
                    label: t("hse.evaluation.view.report"),
                    icon: DocumentTextIcon,
                    path: evaluationPath(record, "report"),
                    hidden: report,
                    menu: false,
                  }
                : null,
            ].filter(Boolean)
          : []
      }
    >
      <div className="mx-auto mt-8 max-w-5xl space-y-6 px-4 sm:px-6 lg:px-8">
        {loading ? (
          <ResourceLoading size="base" />
        ) : error ? (
          <ResourceError errors={[error]} onRetry={load} />
        ) : !record ? (
          <ResourceError errors={[t("hse.evaluation.view.not_found")]} />
        ) : (
          <>
            <section className="rounded-[18px] border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,.04)] sm:p-6">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-lg font-extrabold text-slate-900">{record.title}</h2>
                <span
                  className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${statusTone(
                    record.status
                  )}`}
                >
                  {t(`hse.evaluation.statuses.${record.status}`)}
                </span>
                {record.result ? (
                  <span
                    className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset ${resultTone(
                      record.result
                    )}`}
                  >
                    {t(`hse.evaluation.results.${record.result}`)}
                  </span>
                ) : null}
                <span className="text-sm font-semibold text-slate-600">
                  <EvaluationLabelInfo
                    label={t(`hse.evaluation.types.${record.kind || record.evaluation_type}`)}
                    infoKey={isAudit ? "internal_audit" : "management_review"}
                  />
                </span>
              </div>
              <dl className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Meta label={t("hse.evaluation.fields.scheduled_on")} value={formatDisplayDate(record.scheduled_on)} />
                <Meta label={t("hse.evaluation.fields.held_on")} value={formatDisplayDate(record.held_on)} />
                <Meta label={t("hse.evaluation.fields.next_review_on")} value={formatDisplayDate(record.next_review_on)} />
                <Meta
                  label={t("hse.evaluation.fields.responsible")}
                  value={personLabel(record.responsible_user || record.lead)}
                />
                <Meta label={t("hse.evaluation.fields.period_from")} value={formatDisplayDate(record.period_from)} />
                <Meta label={t("hse.evaluation.fields.period_to")} value={formatDisplayDate(record.period_to)} />
                <Meta
                  label={t("hse.evaluation.tiltak")}
                  value={
                    record.open_tiltak_count
                      ? t("hse.evaluation.tiltak.open_count", { count: record.open_tiltak_count })
                      : "—"
                  }
                />
              </dl>
              {record.scope ? (
                <div className="mt-5">
                  <p className="text-[12px] font-bold uppercase tracking-wide text-slate-400">
                    {t("hse.evaluation.fields.scope")}
                  </p>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{record.scope}</p>
                </div>
              ) : null}
              {record.summary ? (
                <div className="mt-5">
                  <p className="text-[12px] font-bold uppercase tracking-wide text-slate-400">
                    {t("hse.evaluation.fields.summary")}
                  </p>
                  <p className="mt-1 whitespace-pre-line text-sm text-slate-700">{record.summary}</p>
                </div>
              ) : null}
            </section>

            <section className="rounded-[18px] border border-slate-200 bg-white p-5 sm:p-6">
              <h3 className="text-[16px] font-extrabold text-slate-900">
                {t("hse.evaluation.fields.conclusions")}
              </h3>
              <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-700">
                {record.conclusions || t("hse.evaluation.view.no_conclusion")}
              </p>
            </section>

            <EvaluationFindingsPanel evaluation={record} onRefresh={load} />

            {isAudit && !report && (
              <div className="flex justify-end">
                <Link
                  to={evaluationPath(record, "findings")}
                  className="text-sm font-semibold text-[#1463C7] hover:underline"
                >
                  {t("hse.evaluation.view.findings")}
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </HsePageChrome>
  );
}
